<template>
  <div>
    <!-- Show login component only when not authenticated and on a protected route -->
    <login v-if="!isAuthenticated && isProtectedRoute" />
    
    <!-- Show main app when authenticated or on public routes -->
    <div v-else-if="isAuthenticated || !isProtectedRoute" class="flex flex-col h-screen">
      <!-- Show main app content only when authenticated -->
      <div v-if="isAuthenticated" class="flex flex-col flex-1 overflow-hidden relative z-10">
        <app-top-bar-new v-if="redesignUi" />
        <the-header v-else />

        <div
          class="flex w-full flex-1 overflow-hidden relative"
          :class="[redesignUi ? '' : 'pe-5', { 'ps-8': isCollapsed && !redesignUi }]"
        >
          <app-rail-new v-if="redesignUi" />
          <nav-drawer v-else />

          <router-view v-slot="{ Component }">
            <keep-alive :include="['ReportMain']">
              <component :is="Component" />
            </keep-alive>
          </router-view>
          <web-phone />

          <easy-access v-if="hasEasyAccessPermission" />
        </div>
        <redesign-toggle v-if="previewEnabled" />
        <v-container class="pa-0" fluid>
          <progress-bar />
          <status-dialog />
        </v-container>
      </div>
      
      <!-- Show router view for public routes when not authenticated -->
      <router-view v-else />
    </div>
    
    <Toast />
  </div>
</template>

<script>
import TheHeader from "./components/TheHeader.vue";
import ProgressBar from "./components/ProgressDialog.vue";
import StatusDialog from "./components/StatusDialog.vue";
import Login from "./pages/LoginPage.vue";
import config from "../public/config.json";
import { getTenant } from "./services/tenant.js";
//"ws": "ws://localhost:9000/WS",
import { mapGetters, mapActions, mapMutations } from "vuex";
import EasyAccess from "./components/EasyAccess.vue";
import NavDrawer from "./components/NavDrawer.vue";
// import Image from "primevue/image";
import WebPhone from "./pages/call/components/WebPhone.vue";
// import { io } from "socket.io-client";
import { Toast } from "primevue";
import AppRailNew from "./components/redesign/AppRailNew.vue";
import AppTopBarNew from "./components/redesign/AppTopBarNew.vue";
import RedesignToggle from "./components/redesign/RedesignToggle.vue";
import {
  redesignEnabled,
  PREVIEW_ENABLED,
} from "./components/redesign/redesignFlag";

export default {
  name: "App",

  // Exposes the redesign preview flag to the Options API template.
  // PREVIEW_ENABLED is the master switch (currently off — components dormant).
  setup() {
    return { redesignUi: redesignEnabled, previewEnabled: PREVIEW_ENABLED };
  },

  components: {
    TheHeader,
    Login,
    ProgressBar,
    StatusDialog,
    EasyAccess,
    NavDrawer,
    // Image,
    WebPhone,
    Toast,
    AppRailNew,
    AppTopBarNew,
    RedesignToggle,
  },

  data: () => ({
    headerImage: require("./assets/cropped-artboard-bg-colored.jpeg"),
    socket: null,
    closing: false,
    pingTimer: null,
    heartbeatInterval: null,
    reconnectAttempts: 0,
    maxReconnectAttempts: 5,
    baseReconnectDelay: 1000,
    networkOnline: navigator.onLine,
  }),
  computed: {
    ...mapGetters([
      "isAuthenticated",
      "lang",
      "logos",
      "uname",
      "token",
      "notReadChatMessages",
      "notReadOmniMessages",
      "currentChatID",
      "currentOmniID",
      "phonePermission",
      "agentStatusPermission",
      "omniChannelPermission",
      "ManageOmniChannelPermission",
      "permissions",
      "assistantChat",
      "isCollapsed",
      "type",
      "domain",
    ]),
    isProtectedRoute() {
      // Check if current route requires authentication
      return this.$route.meta.requireAuth !== false;
    },
    getDomain() {
      // Post-login the authoritative tenant is the store getter (set from the
      // backend). The getTenant() fallback covers the pre-login case (web: from
      // the URL, desktop: the persisted login value). Depending on the reactive
      // `domain` getter also ensures this recomputes at login rather than
      // caching a stale pre-login value (critical on desktop, where getTenant()
      // reads localStorage and would otherwise never re-evaluate).
      return this.domain || getTenant();
    },
    hasEasyAccessPermission() {
      if (!this.permissions) {
        return false;
      }
      if (
        this.userHasPermisson(this.permissions, this.phonePermission) ||
        this.userHasPermisson(this.permissions, this.agentStatusPermission) ||
        this.userHasPermisson(this.permissions, this.omniChannelPermission) ||
        // this.userHasPermisson(this.permissions, this.assistantBotPermission) || // AI Assistant disabled
        this.userHasPermisson(
          this.permissions,
          this.ManageOmniChannelPermission
        )
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    ...mapActions([
      "localAuthenticate",
      "ShowProgressBar",
      "logout",
      "GetLogos",
    ]),
    ...mapMutations([
      "setLogos",
      "setChatSocket",
      "setSocketKey",
      "setNotReadChatMessages",
      "setIncomingSocketMessage",
      "setNotReadOmniMessages",
      "setIncomingSocketOmniMessage",
      "setDifferentTABSent",
      "setOmniDifferentTABSent",
      "setDifferentTABRead",
      "setOmniDifferentTABRead",
      "setOmniConversationClosed",
      "setAssignedOmniConversation",
      "setOmniFailedMsg",
      "setAssistantChat",
      "setAssistantAsk",
      "setIsWhispering",
      "setAutoAnswer",
    ]),
    setupWebSocket() {
      const params =
        "?username=" +
        this.uname +
        "&sessionid=" +
        this.token +
        "&domain=" +
        this.getDomain +
        "&request=socketRequest";
      this.socket = new WebSocket(config.ws + params);
      this.socket.onopen = this.onSocketOpen;
      this.socket.onmessage = this.onSocketMessage;
      this.socket.onerror = this.onSocketError;
      this.socket.onclose = this.onSocketClose;
      
      // Start heartbeat when connection is established
      this.startHeartbeat();
    },
    onSocketOpen(ev) {
      console.log("CONNECTED:", ev);
      // Reset reconnection attempts on successful connection
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      
      // Trigger WebRTC health check when app WebSocket reconnects
      this.checkWebRTCHealthAfterReconnect();
    },
    onSocketClose(ev) {
      console.log("Closed:", ev);
      this.stopHeartbeat();
      
      if (!this.closing && this.isAuthenticated) {
        this.attemptReconnection();
      }
    },
    onSocketMessage(ev) {
      // console.log("My message from Server", ev);
      const data = JSON.parse(ev.data);
      if (!data) {
        return;
      }
      //{"message_id":"","from":"","to":"","text":"not aut…nverse_id":"","time":"","type":2,"success":false}
      switch (data.type) {
        //PEER TO PEER Message
        case 1:
          if (!data.success) {
            return;
          }
          this.handleIncomingMSG(data);
          break;
        //INIT AUTH CONN Message
        case 2:
          if (data.success) {
            this.setChatSocket(this.socket);
            this.setSocketKey(data.to);
            this.socket.send(JSON.stringify({ type: 3 }));
          } else {
            this.pingTimer = setTimeout(() => {
              this.setupWebSocket();
            }, 6000);
          }
          break;
        // Get Unread Request
        case 3:
          break;
        // Update Read Message
        case 4:
          break;
        //Update Read From different TAB
        case 5:
          this.handleDifferentTABRead(data);
          break;
        //Sent Message From different TAB
        case 6:
          this.handleDifferentTABSent(data);
          break;
        //Receive Omni Channel Message
        case 31:
          if (!data.success) {
            this.setOmniFailedMsg(data);
          }
          break;
        //Receive Omni Channel Message
        case 32:
          this.handleIncomingOmniMSG(data);
          break;
        //Update Read Omni From different TAB
        case 35:
          this.handleDifferentOmniTABRead(data);
          break;
        //Sent Omni From different TAB
        case 36:
          this.setOmniDifferentTABSent(data);
          // this.handleDifferentOmniTABSent(data);
          break;
        //Closed Omni Conversation
        case 37:
          // console.log("My 37 sett", 37);
          this.setOmniConversationClosed(data);
          break;
        //Reassign Omni Conversation
        case 38:
          // console.log("My 38 sett", 38);
          this.setAssignedOmniConversation(data);
          break;
        //Sent Message To Assistant
        case 41:
          this.handleSentMessageToAssistant(data);
          break;
        //Receive message from Assistant
        case 42:
          this.handleReceivedAssistantMessage(data);
          break;
        //Differnt Tab Sent message to assistant
        case 43:
          this.handleDifferntTabAssistantMessage(data);
          break;
        //Sla Alerting
        case 51:
          console.log("My sla alerting data", data);
          if (data.string == this.uname) {
            this.$toast.add({
              severity: "error",
              summary: "SLA Alert",
              detail: data.message,
            });
          }
          break;
        //Whispering
        case 52:
          if (this.type === 1) {
            this.setIsWhispering(true);
          }
          break;
        case 53:
          if (this.type === 1) {
            this.setIsWhispering(false);
          }
          break;
        //Update Auto Answer
        case 54: {
          const isAutoAnswer = data.message == "1" ? true : false;
          this.setAutoAnswer(isAutoAnswer);
          if (window.WebRTCService) {
            window.WebRTCService.setAutoAnswerConfig(isAutoAnswer);
          }
          break;
        }
      }
    },
    onSocketError(ev) {
      console.log("My error from Server", ev);
    },
    
    // Start heartbeat to detect connection health
    startHeartbeat() {
      this.stopHeartbeat(); // Clear any existing heartbeat
      
      this.heartbeatInterval = setInterval(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          console.log('Sending heartbeat ping');
          this.socket.send(JSON.stringify({ type: 'ping' }));
        } else {
          console.log('WebSocket not open, stopping heartbeat');
          this.stopHeartbeat();
        }
      }, 30000); // Send heartbeat every 30 seconds
    },
    
    // Stop heartbeat
    stopHeartbeat() {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    },
    
    // Attempt reconnection with exponential backoff
    attemptReconnection() {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.log('Max reconnection attempts reached');
        return;
      }
      
      this.reconnectAttempts++;
      const delay = this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      
      console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
      
      this.pingTimer = setTimeout(() => {
        this.setupWebSocket();
      }, delay);
    },
    
    // Setup network change detection
    setupNetworkDetection() {
      window.addEventListener('online', () => {
        console.log('Network came online');
        this.networkOnline = true;
        this.handleNetworkOnline();
      });
      
      window.addEventListener('offline', () => {
        console.log('Network went offline');
        this.networkOnline = false;
        this.handleNetworkOffline();
      });
    },
    
    // Handle network coming online
    handleNetworkOnline() {
      if (this.isAuthenticated && !this.socket) {
        console.log('Network online - reconnecting WebSocket');
        this.setupWebSocket();
      }
    },
    
    // Handle network going offline
    handleNetworkOffline() {
      console.log('Network offline - connection may be lost');
      // Don't close socket immediately, let it try to reconnect
    },
    
    // Check WebRTC health after app WebSocket reconnects
    checkWebRTCHealthAfterReconnect() {
      // Wait a moment for the connection to stabilize
      setTimeout(() => {
        console.log('App WebSocket reconnected - checking WebRTC health');
        
        // Emit an event that the WebPhone component can listen to
        this.$eventBus?.emit?.('app-websocket-reconnected');
        
        // Alternative: Direct check if we have access to WebRTC service
        if (window.WebRTCService) {
          const isConnected = window.WebRTCService.isConnected();
          const isRegistered = window.WebRTCService.isRegistered();
          
          console.log('WebRTC health after app reconnect:', { isConnected, isRegistered });
          
          if (!isConnected || !isRegistered) {
            console.log('WebRTC needs attention after app reconnect');
            // We can't directly call initPhone from here, but we can set a flag
            // that the WebPhone component will detect
            this.$store?.commit?.('setWebRTCNeedsCheck', true);
          }
        }
      }, 2000); // Wait 2 seconds for connection stability
    },
    handleIncomingMSG(newMessage) {
      // const msgChatIsOpen =
      //   this.currentChatID == newMessage.conversation_id;
      // console.log("My current chat ID", this.currentChatID);
      if (this.currentChatID == newMessage.conversation_id) {
        this.socket.send(
          JSON.stringify({
            type: 4,
            not_read: [newMessage.message_id],
            conversation_id: newMessage.conversation_id,
          })
        );
        this.setIncomingSocketMessage(newMessage);
      } else {
        const unreadMessages = this.notReadChatMessages
          ? JSON.parse(JSON.stringify(this.notReadChatMessages))
          : [];
        const idx = unreadMessages.findIndex(
          (message) => newMessage.message_id == message.message_id
        );
        if (idx == -1) {
          unreadMessages.push(newMessage);
          // console.log(
          //   "My messages and unread messages",
          //   unreadMessages,
          //   newMessage
          // );
          this.setNotReadChatMessages(unreadMessages);
        }
      }
    },
    handleDifferentTABSent(messageSent) {
      // console.log("My handleDifferentTABSent!!!");
      // if (
      //   this.currentChatID &&
      //   this.currentChatID?.conversation_id == messageSent.conversation_id
      // ) {
      //   const converse = JSON.parse(JSON.stringify(this.currentChatID));
      //   const msgIDX = converse.messages.findIndex(
      //     (message) => messageSent.message_id == message.id
      //   );
      //   if (msgIDX == -1) {
      //     const msgToPush = {
      //       message: messageSent.text,
      //       id: messageSent.message_id,
      //       time: messageSent.time,
      //       type: 1,
      //       is_read: true,
      //     };
      //     converse.messages.unshift(msgToPush);
      //     this.setIncomingSocketMessage(converse);
      //     // this.socket.send(
      //     //   JSON.stringify({ type: 4, not_read: [messageSent.message_id] })
      //     // );
      //   }
      //   return;
      // }
      this.setDifferentTABSent(messageSent);
    },
    handleDifferentOmniTABSent(messageSent) {
      // handleDifferentOmniTABSent(messageSent) {
      this.setOmniDifferentTABSent(messageSent);
    },
    handleDifferentTABRead(sentMessage) {
      const unreadMessages = this.notReadChatMessages
        ? this.notReadChatMessages.filter(
            (msg) => !sentMessage.not_read.includes(msg.message_id)
          )
        : [];
      this.setNotReadChatMessages(unreadMessages);
      // const idx = unreadMessages.findIndex(
      //   (message) => newMessage.message_id == message.message_id
      // );
      this.setDifferentTABRead(sentMessage);
    },
    handleDifferentOmniTABRead(sentMessage) {
      // console.log("My handleDifferentTABRead!!!", sentMessage);
      const unreadMessages = this.notReadOmniMessages
        ? this.notReadOmniMessages.filter(
            (msg) => !sentMessage.not_read.includes(msg.message_id)
          )
        : [];
      this.setNotReadOmniMessages(unreadMessages);
      // const idx = unreadMessages.findIndex(
      //   (message) => newMessage.message_id == message.message_id
      // );
      this.setOmniDifferentTABRead(sentMessage);
    },
    handleIncomingOmniMSG(newMessage) {
      if (this.currentOmniID == newMessage.conversation_id) {
        this.socket.send(
          JSON.stringify({
            type: 34,
            not_read: [newMessage.message_id],
            conversation_id: newMessage.conversation_id,
          })
        );
        this.setIncomingSocketOmniMessage(newMessage);
      } else {
        // console.log("My handle incoming")
        const unreadMessages = this.notReadOmniMessages
          ? JSON.parse(JSON.stringify(this.notReadOmniMessages))
          : [];
        const idx = unreadMessages.findIndex(
          (message) => newMessage.message_id == message.message_id
        );
        if (idx == -1) {
          unreadMessages.push(newMessage);
          this.setNotReadOmniMessages(unreadMessages);
        }
      }
    },
    handleSentMessageToAssistant(message) {
      const updatedMsgs = JSON.parse(
        JSON.stringify(this.assistantChat.messages)
      );
      const msgIdx = updatedMsgs.findIndex(
        (msg) => msg.id == message.message_id
      );
      if (msgIdx != -1) {
        updatedMsgs[msgIdx].waiting = false;
        updatedMsgs[msgIdx].loading = false;
        updatedMsgs[msgIdx].failed = !message.success;
        this.setAssistantChat({
          ...this.assistantChat,
          messages: updatedMsgs,
        });
      }
    },
    handleReceivedAssistantMessage(message) {
      // Adopt the conversation id assigned by the backend so subsequent
      // messages reuse it instead of the temporary id generated on the
      // frontend when a conversation had not started yet.
      const backendConversationId =
        message.conversation_id || message.converse_id || "";
      if (
        this.assistantChat &&
        backendConversationId &&
        this.assistantChat.conversation_id !== backendConversationId
      ) {
        this.setAssistantChat({
          ...this.assistantChat,
          conversation_id: backendConversationId,
        });
      }
      const replyMessage = {
        message: message.text,
        time: message.time,
        type: 2,
        id: message.message_id,
      };
      this.setAssistantAsk(replyMessage);
      setTimeout(() => {
        const updatedMsgs = JSON.parse(
          JSON.stringify(this.assistantChat.messages)
        );
        const msgIdx = updatedMsgs.findIndex(
          (msg) => msg.id == message.message_id
        );
        if (msgIdx == -1) {
          updatedMsgs.unshift(replyMessage);
          this.setAssistantChat({
            ...this.assistantChat,
            messages: updatedMsgs,
          });
        }
      }, 800);
    },
    handleDifferntTabAssistantMessage(message) {
      const backendConversationId =
        message.conversation_id || message.converse_id || "";
      if (
        this.assistantChat &&
        backendConversationId &&
        this.assistantChat.conversation_id !== backendConversationId
      ) {
        this.setAssistantChat({
          ...this.assistantChat,
          conversation_id: backendConversationId,
        });
      }
      const replyMessage = {
        message: message.text,
        time: message.time,
        type: 1,
        id: message.message_id,
      };
      this.setAssistantAsk(replyMessage);
      setTimeout(() => {
        const updatedMsgs = JSON.parse(
          JSON.stringify(this.assistantChat.messages)
        );
        const msgIdx = updatedMsgs.findIndex(
          (msg) => msg.id == message.message_id
        );
        if (msgIdx == -1) {
          updatedMsgs.unshift(replyMessage);
          this.setAssistantChat({
            ...this.assistantChat,
            messages: updatedMsgs,
          });
        }
      }, 800);
    },
    clearing() {
      if (this.socket) {
        this.closing = true;
        this.socket.close();
        this.setChatSocket(null);
      }
      this.stopHeartbeat();
      clearTimeout(this.pingTimer);
      this.pingTimer = null;
      this.reconnectAttempts = 0;
      
      // Reset WebRTC synchronization flag
      this.$store?.commit?.('setWebRTCNeedsCheck', false);
      
      // Remove network event listeners
      window.removeEventListener('online', this.handleNetworkOnline);
      window.removeEventListener('offline', this.handleNetworkOffline);
    },
  },
  async created() {
    var resp = this.localAuthenticate();
    if (!resp) {
      await this.logout();
    }

    if (!this.logos) {
      await this.GetLogos(this.getDomain);
    }

    if (this.lang) {
      const html = document.documentElement;
      html.dir = this.lang === "ar" ? "rtl" : "ltr";
      this.$vuetify.locale.current = this.lang;
    }
    
    // Setup network change detection
    this.setupNetworkDetection();
    
    // const notRead = localStorage.getItem("chat-notify");
    // if (notRead) {
    //   this.setNotReadChatMessages(notRead);
    // } else {
    //   this.setNotReadChatMessages([]);
    // }
  },
  watch: {
    lang(value) {
      if (value) {
        this.$vuetify.locale.current = value;
      }
    },
    isAuthenticated(value) {
      if (value) {
        this.closing = false;
        this.setupWebSocket();
      } else {
        this.clearing();
      }
    },
  },
  beforeCreate() {},
  unmounted() {
    this.clearing();
  },
};
</script>

<style>
html {
  font-family: ReadexPro !important;
}
</style>
